import React, { useState, useEffect } from "react";
import supabase from "../../supabaseClient/supabaseClient";
import { useNavigate } from "react-router-dom";
import "./loginPage.css";
import Logo from "../Navbar/sheep.svg";
import {
  EMAIL_CONFIRMED_REDIRECT_URL,
  rememberPendingSignupEmail,
  resendConfirmationEmail,
} from "./emailConfirmation";

const Login = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("signin");
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  // Email we've just sent a confirmation link to; shows the "check your email" screen.
  const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState("");
  const [resendMessage, setResendMessage] = useState({ text: "", type: "" });
  const [resending, setResending] = useState(false);

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      // Handle different auth events
      if (event === "SIGNED_IN") {
        navigate("/", { replace: true });
        setMessage({ text: "Successfully signed in!", type: "success" });
      } else if (event === "SIGNED_OUT") {
        setMessage({ text: "Successfully signed out!", type: "success" });
      } else if (event === "PASSWORD_RECOVERY") {
        setMessage({
          text: "Check your email for password reset instructions",
          type: "success",
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const clearMessage = (seconds = 60) => {
    setTimeout(() => setMessage({ text: "", type: "" }), seconds * 1000);
  };



  // Email validation helper
  const isValidEmail = (email) => {
    // Simple regex for email validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);

    // 0️⃣ Validate email format
    if (!isValidEmail(formData.email)) {
      setMessage({ text: "Please enter a valid email address.", type: "error" });
      setLoading(false);
      clearMessage(5);
      return;
    }

    // 1️⃣ Validate password match
    if (formData.password !== formData.confirmPassword) {
      setMessage({ text: "Passwords do not match", type: "error" });
      setLoading(false);
      clearMessage(5);
      return;
    }

    try {
      // 2️⃣ Proceed with signup
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: EMAIL_CONFIRMED_REDIRECT_URL,
        },
      });

      if (error) throw error;

      // Supabase returns a user with no identities when the email is already registered
      if (data.user && data.user.identities?.length === 0) {
        setMessage({
          text: "This email is already registered. Please sign in instead.",
          type: "error",
        });
        clearMessage(10);
        return;
      }

      if (data.user && !data.user.email_confirmed_at) {
        rememberPendingSignupEmail(formData.email);
        setPendingConfirmationEmail(formData.email);
        setResendMessage({ text: "", type: "" });
      } else {
        // fallback for other cases
        setMessage({
          text: "Sign up successful.",
          type: "success",
        });
        clearMessage(60);
      }

      setFormData({ email: "", password: "", confirmPassword: "" });
    } catch (error) {
      setMessage({ text: error.message, type: "error" });
      clearMessage(60);
    } finally {
      setLoading(false);
    }
  };
  

  const handleResendConfirmation = async () => {
    setResending(true);
    try {
      const { error } = await resendConfirmationEmail(pendingConfirmationEmail);
      if (error) throw error;
      setResendMessage({ text: "We've sent you a new confirmation link.", type: "success" });
    } catch (error) {
      setResendMessage({ text: error.message, type: "error" });
    } finally {
      setResending(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    // 0️⃣ Validate email format
    if (!isValidEmail(formData.email)) {
      setMessage({ text: "Please enter a valid email address.", type: "error" });
      setLoading(false);
      clearMessage(5);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;

      setFormData({ email: "", password: "", confirmPassword: "" });
    } catch (error) {
      setMessage({ text: error.message, type: "error" });
    } finally {
      setLoading(false);
      clearMessage(10);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/login`,
        },
      });

      if (error) throw error;
    } catch (error) {
      setMessage({ text: error.message, type: "error" });
      clearMessage(10);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        formData.email,
        {
          redirectTo: `https://app.theworksheep.com/reset-password`,
        }
      );

      if (error) throw error;

      setMessage({
        text: "Password reset email sent! Check your inbox for instructions.",
        type: "success",
      });
      setFormData({ email: "", password: "", confirmPassword: "" });
    } catch (error) {
      setMessage({ text: error.message, type: "error" });
    } finally {
      setLoading(false);
      clearMessage(60);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      setMessage({ text: error.message, type: "error" });
      clearMessage(10);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (user) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="user-dashboard">
            <h2>Welcome!</h2>
            <p className="welcome-text">You are signed in as:</p>
            <div className="user-info">
              <p className="user-email">{user.email}</p>
              <p className="user-id">ID: {user.id}</p>
            </div>
            <button onClick={handleSignOut} className="btn btn-danger">
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="brand-content">
          <h2>Welcome to Worksheep</h2>
          <div className="logo-container">
            <img src={Logo} alt="Worksheep Logo" className="worksheep-logo" />
          </div>
        </div>


        {/* Message Display (hidden while the signup tab shows the "check your email" screen) */}
        {!(currentView === "signup" && pendingConfirmationEmail) &&
          message.text && (
            <div className={`message ${message.type}`}>{message.text}</div>
        )}





        {/* Navigation Tabs */}
        <div className="auth-tabs">
          <button
            className={`tab-button ${currentView === "signin" ? "active" : ""}`}
            onClick={() => setCurrentView("signin")}
          >
            Sign In
          </button>
          <button
            className={`tab-button ${currentView === "signup" ? "active" : ""}`}
            onClick={() => setCurrentView("signup")}
          >
            Sign Up
          </button>
          <button
            className={`tab-button ${currentView === "reset" ? "active" : ""}`}
            onClick={() => setCurrentView("reset")}
          >
            Reset Password
          </button>
        </div>

        {/* Sign In Form */}
        {currentView === "signin" && (
          <div className="auth-form">
            <h2>Sign In</h2>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "View"}
                </button>
              </div>
            </div>

            <button
              onClick={handleSignIn}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>

            <div className="divider">
              <span>or</span>
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="btn btn-google"
            >
              <img
                src="https://www.google.com/favicon.ico"
                alt="Google"
                className="google-icon"
              />
              Continue with Google
            </button>
          </div>
        )}

        {/* Sign Up Form or Success Message */}
        {currentView === "signup" && (
          pendingConfirmationEmail ? (
            <div className="auth-form">
              <h2>Check your email</h2>
              <div className="message success" style={{ textAlign: "center" }}>
                We've sent a confirmation link to <strong>{pendingConfirmationEmail}</strong>. Click the
                link in that email to confirm your account — you'll be signed in automatically.
              </div>
              <p className="confirm-text">
                Can't find it? Check your spam or quarantine folder and make sure emails from{" "}
                <strong>theworksheep.com</strong> are allowed.
              </p>
              {resendMessage.text && (
                <div className={`message ${resendMessage.type}`}>{resendMessage.text}</div>
              )}
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resending}
                className="btn btn-success"
              >
                {resending ? "Sending..." : "Resend confirmation email"}
              </button>
            </div>
          ) : (
            <div className="auth-form">
              <h2>Sign Up</h2>

              <div className="form-group email-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onFocus={() => setIsEmailFocused(true)}
                  onBlur={() => setIsEmailFocused(false)}
                  required
                  placeholder="Enter your email"
                />
                {isEmailFocused && (
                  <div className="email-info">
                    To avoid experiencing delivery issues, please check your spam or quarantine folder and ensure emails from <strong>theworksheep.com</strong> are permitted.
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-input">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={6}
                    placeholder="Create a password (min. 6 characters)"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "View"}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <div className="password-input">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "Hide" : "View"}
                  </button>
                </div>
              </div>

              <button
                onClick={handleSignUp}
                disabled={loading}
                className="btn btn-success"
              >
                {loading ? "Creating Account..." : "Sign Up"}
              </button>
            </div>
          )
        )}

        {/* Password Reset Form */}
        {currentView === "reset" && (
          <div className="auth-form">
            <h2>Reset Password</h2>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <button
              onClick={handlePasswordReset}
              disabled={loading}
              className="btn btn-warning"
            >
              {loading ? "Sending Reset Email..." : "Send Reset Email"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
